import React, { useEffect, useState } from "react";
import { fetchOrdersParent, fetchDtdcTracking } from "../../../actions/product";
import { useSelector } from "react-redux";
import FullPageSpinner from "../../layout/FullPageSpinner";
import { downloadPaymentReceipt } from "../../../utils/pdfGenerator";
import { toast } from "react-toastify";
import { Modal, Button, Table } from "react-bootstrap";

const OrderHistory = () => {
  const { user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [sortOrder, setSortOrder] = useState("Latest");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [downloadingIds, setDownloadingIds] = useState(new Set());
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [fetchingTracking, setFetchingTracking] = useState(false);
  const [selectedOrderForTracking, setSelectedOrderForTracking] = useState(null);

  useEffect(() => {
    const getLinkedOrders = async () => {
      setLoading(true);
      try {
        if (user) {
          const response = await fetchOrdersParent(user.id);
          if (response.success) {
            setOrders(response.orders);
          }
        } else {
          setOrders([]);
        }
      } catch (error) {
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };

    getLinkedOrders();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSortChange = (e) => {
    const order = e.target.value;
    setSortOrder(order);
    const sortedOrders = [...orders].sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);

      if (order === "Latest") {
        return dateB - dateA; // Newest first
      } else {
        return dateA - dateB; // Oldest first
      }
    });

    setOrders(sortedOrders);
  };

  const handleDownloadReceipt = async (orderId) => {
    // Log the specific order data to see what's being passed
    // const currentOrder = orders.find((order) => order.id === orderId);
    try {
      setDownloadingIds((prev) => new Set([...prev, orderId]));
      await downloadPaymentReceipt(orderId);
      toast.success("Receipt downloaded successfully!", { position: "top-right" });
    } catch (error) {
      toast.error("Failed to download receipt. Please try again.", { position: "top-right" });
      console.error("Download error:", error);
    } finally {
      setDownloadingIds(prev => {
        const newSet = new Set([...prev]);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  const handleViewTracking = async (order) => {
    setSelectedOrderForTracking(order);
    setFetchingTracking(true);
    try {
      const data = await fetchDtdcTracking(order.id);
      setTrackingData(data);
      setShowTrackingModal(true);
    } catch (error) {
      console.error("Error fetching tracking details:", error);
      toast.error("Failed to fetch tracking details");
    } finally {
      setFetchingTracking(false);
    }
  };

  const getSimplifiedStatus = (status) => {
    if (!status) return "-";
    
    const mapping = {
      "IN_PROGRESS": "IN_PROGRESS",
      "ARRIVED_AT_WAREHOUSE": "IN_PROGRESS",
      "HOLD_AT_WAREHOUSE": "IN_PROGRESS",
      "PACKED": "IN_PROGRESS",
      "SHIPPED": "SHIPPED",
      "OUT_FOR_DELIVERY": "OUT_FOR_DELIVERY",
      "DELIVERY_IN_PROGRESS": "OUT_FOR_DELIVERY",
      "ATTEMPTED": "OUT_FOR_DELIVERY",
      "HELDUP": "OUT_FOR_DELIVERY",
      "RTO": "OUT_FOR_DELIVERY",
      "PARTIALLY_DELIVERED": "DELIVERED",
      "DELIVERED": "DELIVERED"
    };

    const simplified = mapping[status] || status;
    return simplified.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  return loading ? (
    <FullPageSpinner loading={loading} />
  ) : (
    <div className="bg-light min-vh-100 py-4">
      <div className="container pb-5">
        <div className="card shadow-sm border-0 rounded-3">
          <div className="card-body p-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="text-primary fw-bold mb-0">
                <i className="bi bi-clock-history me-2"></i>Order History
              </h5>
              <div className="d-flex align-items-center">
                <span className="text-muted me-2 small">Sort by:</span>
                <select
                  className="form-select form-select-sm w-auto"
                  value={sortOrder}
                  onChange={handleSortChange}
                >
                  <option value="Latest">Latest</option>
                  <option value="Oldest">Oldest</option>
                </select>
              </div>
            </div>

            {/* Order Table */}
            <div className="table-responsive">
              <table className="table table-hover align-middle text-center mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="border-0">#</th>
                    <th className="border-0">Order ID</th>
                    <th className="border-0">Student Details</th>
                    <th className="border-0">Payment</th>
                    <th className="border-0">Delivery Status</th>
                    <th className="border-0">Amount</th>
                    <th className="border-0">Date</th>
                    <th className="border-0">View</th>
                    <th className="border-0">Track</th>
                    <th className="border-0">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.length > 0 ? (
                    orders.slice(0, itemsPerPage).map((order, index) => (
                      <tr key={order.id}>
                        <td className="text-muted small">#{index + 1}</td>
                        <td className="fw-semibold text-nowrap">{order.orderId}</td>
                        <td>
                          <div className="fw-semibold text-dark">{order.studentNames}</div>
                          <div className="text-muted small">{order.studentUSIDs}</div>
                        </td>
                        <td>
                          <span className={`badge rounded-pill ${
                            order.transactionStatus === "PAID"
                              ? "bg-success-subtle text-success border border-success-subtle"
                              : "bg-danger-subtle text-danger border border-danger-subtle"
                          }`}>
                            {order.transactionStatus}
                          </span>
                        </td>
                        <td>
                          <span className="badge rounded-pill bg-info-subtle text-info-emphasis border border-info-subtle">
                            {getSimplifiedStatus(order.status)}
                          </span>
                        </td>
                        <td className="fw-bold text-dark text-nowrap">₹{order.totalPrice}</td>
                        <td className="text-nowrap small text-muted">
                          {order.createdAt?.split("T")[0]}
                        </td>
                        <td className="text-nowrap">
                          {['SHIPPED', 'OUT_FOR_DELIVERY', 'DELIVERY_IN_PROGRESS', 'ATTEMPTED', 'RTO'].includes(getSimplifiedStatus(order.status)?.toUpperCase()) && order.trackingId ? (
                            <div className="d-flex align-items-center gap-2">
                              <button
                                onClick={() => handleViewTracking(order)}
                                disabled={
                                  fetchingTracking &&
                                  selectedOrderForTracking?.id === order.id
                                }
                                className="btn btn-sm btn-link text-primary text-decoration-none fw-semibold small p-0"
                              >
                                {fetchingTracking &&
                                selectedOrderForTracking?.id === order.id ? (
                                  <span className="spinner-border spinner-border-sm me-1"></span>
                                ) : (
                                  <i className="bi bi-geo-alt me-1"></i>
                                )}
                                {order.trackingId}
                              </button>
                            </div>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td className="text-nowrap">
                          {['SHIPPED', 'OUT_FOR_DELIVERY', 'DELIVERY_IN_PROGRESS', 'ATTEMPTED', 'RTO'].includes(getSimplifiedStatus(order.status)?.toUpperCase()) && order.trackingId ? (
                            <div className="d-flex align-items-center gap-2">
                              <a
                                href={`https://www.dtdc.com/track-your-shipment/?awb=${order.trackingId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary text-decoration-none fw-semibold small"
                              >
                                DTDC
                                <i className="bi bi-box-arrow-up-right ms-1"></i>
                              </a>
                            </div>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          {order.transactionStatus === "PAID" ? (
                            <button
                              onClick={() => handleDownloadReceipt(order.id)}
                              disabled={downloadingIds.has(order.id)}
                              className="btn btn-sm btn-outline-primary rounded-pill px-3 d-flex align-items-center gap-1 mx-auto"
                            >
                              {downloadingIds.has(order.id) ? (
                                <span className="spinner-border spinner-border-sm"></span>
                              ) : (
                                <>
                                  <i className="bi bi-download"></i>
                                  <span className="small">Receipt</span>
                                </>
                              )}
                            </button>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center text-muted fw-bold py-5">
                        <i className="bi bi-inbox fs-1 d-block mb-2 text-light"></i>
                        No orders found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="d-flex justify-content-end align-items-center mt-4 pt-3 border-top">
              <span className="text-muted me-2 small">Items per page:</span>
              <select
                className="form-select form-select-sm w-auto"
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      <TrackingDetailsModal
        show={showTrackingModal}
        onHide={() => setShowTrackingModal(false)}
        trackingData={trackingData}
        order={selectedOrderForTracking}
      />
    </div>
  );
};

const TrackingDetailsModal = ({ show, onHide, trackingData, order }) => {
  const steps = ["Picked Up", "Accepted", "In Transit", "At Destination", "Delivered"];
  
  const getActiveStep = (status) => {
    if (!status) return -1;
    const s = status.toUpperCase();
    if (s.includes("DELIVERED")) return 4;
    if (s.includes("DESTINATION") || s.includes("OUT FOR DELIVERY")) return 3;
    if (s.includes("TRANSIT") || s.includes("SHIPPED")) return 2;
    if (s.includes("ACCEPTED") || s.includes("ARRIVED") || s.includes("PACKED")) return 1;
    if (s.includes("PICKED") || s.includes("PICKUP")) return 0;
    return -1;
  };

  const activeStep = trackingData ? getActiveStep(trackingData.trackHeader?.strStatus) : -1;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered className="tracking-modal">
      <style>{`
        .tracking-modal .modal-content { border-radius: 15px; border: none; box-shadow: 0 10px 30px rgba(0,0,0,0.1); }
        .tracking-modal .modal-header { border-bottom: 1px solid #f0f0f0; padding: 20px 25px; }
        .tracking-modal .modal-title { color: #0d6efd; font-weight: 700; }
        .tracking-modal .modal-body { padding: 25px; }
        .info-card { background: #f8f9fa; border-radius: 12px; padding: 20px; margin-bottom: 25px; border: 1px solid #eee; }
        .info-label { color: #6c757d; font-size: 0.75rem; font-weight: 600; text-transform: uppercase; margin-bottom: 4px; }
        .info-value { color: #212529; font-weight: 600; font-size: 0.95rem; }
        
        /* Stepper Styles */
        .stepper { display: flex; justify-content: space-between; margin: 40px 0 30px; position: relative; }
        .stepper::before { content: ''; position: absolute; top: 15px; left: 0; right: 0; height: 3px; background: #e9ecef; z-index: 1; }
        .step { position: relative; z-index: 2; display: flex; flex-direction: column; align-items: center; width: 20%; }
        .step-icon { width: 32px; height: 32px; border-radius: 50%; background: #fff; border: 3px solid #e9ecef; margin-bottom: 10px; transition: all 0.3s; display: flex; align-items: center; justify-content: center; font-size: 0.8rem; }
        .step.active .step-icon { border-color: #0d6efd; color: #0d6efd; background: #fff; }
        .step.completed .step-icon { background: #0d6efd; border-color: #0d6efd; color: #fff; }
        .step-label { font-size: 0.7rem; font-weight: 600; color: #6c757d; text-align: center; }
        .step.active .step-label { color: #0d6efd; }
        .step.completed .step-label { color: #212529; }
        
        .stepper-progress { position: absolute; top: 15px; left: 0; height: 3px; background: #0d6efd; z-index: 1; transition: width 0.5s ease; }
        
        .history-table th { font-size: 0.75rem; text-transform: uppercase; color: #6c757d; border-top: none; }
        .history-table td { font-size: 0.85rem; padding: 12px 8px; }
      `}</style>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-truck me-2"></i>Track Your Order
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {trackingData && trackingData.status === "SUCCESS" ? (
          <div>
            <div className="info-card">
              <div className="row g-3">
                <div className="col-6 col-md-3">
                  <div className="info-label">Reference No</div>
                  <div className="info-value text-truncate">{order?.orderId}</div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="info-label">Tracking No</div>
                  <div className="info-value">{trackingData.trackHeader?.strcnno}</div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="info-label">Booked On</div>
                  <div className="info-value">{trackingData.trackDetails?.[trackingData.trackDetails.length - 1]?.strActionDate || "-"}</div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="info-label">Status</div>
                  <div className="info-value"><span className="text-primary">{trackingData.trackHeader?.strStatus}</span></div>
                </div>
                <div className="col-12 mt-3 pt-3 border-top">
                  <div className="row">
                    <div className="col-6">
                      <div className="info-label">Origin</div>
                      <div className="info-value">{trackingData.trackHeader?.strOrigin}</div>
                    </div>
                    <div className="col-6">
                      <div className="info-label">Destination</div>
                      <div className="info-value">{trackingData.trackHeader?.strDest}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <h6 className="fw-bold mb-4 px-1">Track Progress</h6>
            <div className="stepper">
              <div className="stepper-progress" style={{ width: `${(activeStep / (steps.length - 1)) * 100}%` }}></div>
              {steps.map((step, index) => (
                <div key={index} className={`step ${index === activeStep ? 'active' : ''} ${index < activeStep ? 'completed' : ''}`}>
                  <div className="step-icon">
                    {index < activeStep ? <i className="bi bi-check-lg"></i> : index + 1}
                  </div>
                  <div className="step-label">{step}</div>
                </div>
              ))}
            </div>

            <div className="mt-5">
              <h6 className="fw-bold mb-3 px-1">Tracking History</h6>
              <div className="table-responsive">
                <Table hover className="history-table">
                  <thead>
                    <tr>
                      <th>Date & Time</th>
                      <th>Location</th>
                      <th>Activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trackingData.trackDetails && trackingData.trackDetails.length > 0 ? (
                      trackingData.trackDetails.map((detail, index) => (
                        <tr key={index}>
                          <td className="text-nowrap">
                            <div className="fw-semibold">{detail.strActionDate}</div>
                            <div className="text-muted small">{detail.strActionTime}</div>
                          </td>
                          <td>{detail.strLocation}</td>
                          <td>{detail.strAction}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center text-muted py-4">No history available</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-5">
            <i className="bi bi-exclamation-circle fs-1 text-muted mb-3 d-block"></i>
            <h5 className="text-dark fw-bold">No Tracking Info Found</h5>
            <p className="text-muted">
              {trackingData?.message || "We couldn't retrieve tracking information for this order at the moment. Please try again later."}
            </p>
            <Button variant="outline-primary" onClick={onHide} className="mt-2 rounded-pill px-4">Close</Button>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="border-0 pb-4 justify-content-center gap-3">
         <div className="d-flex align-items-center gap-2 text-muted small cursor-pointer">
            <i className="bi bi-chat-dots fs-5 text-primary"></i>
            <span>Chat with Support</span>
         </div>
         <div className="vr mx-2"></div>
         <div className="d-flex align-items-center gap-2 text-muted small cursor-pointer">
            <i className="bi bi-exclamation-triangle fs-5 text-danger"></i>
            <span>Raise a Dispute</span>
         </div>
      </Modal.Footer>
    </Modal>
  );
};

export default OrderHistory;
